import { Injectable, inject } from '@angular/core';
import { RichVerifyDialogComponent } from '@app/modules/dialogs/rich-verify-phone-dialog/rich-verify-dialog.component';
import { LoginResponse, UsersService } from '@app/services/users.service';
import { ContactsService } from '@app/services/contacts.service';
import { RecaptchaService } from '@app/services/recaptcha-service';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogService } from '@app/modules/dialogs/dialog.service';
import { MatDialogRef } from '@angular/material/dialog';

@Injectable({
    providedIn: 'root',
})
export class AuthenticateEmailService {
    private readonly userService = inject(UsersService);
    private readonly contactService = inject(ContactsService);
    private readonly recaptchaService = inject(RecaptchaService);
    private readonly dialogService = inject(DialogService);

    public async login(email: string, contactId: string): Promise<LoginResponse> {
        try {
            const gcToken = await this.recaptchaService.check('contacts/verify');
            // send verification email
            await this.contactService.verifyCaptcha({
                contactId,
                verifyEmail: true,
                gcToken,
            });
        } catch (e) {
            if (e instanceof HttpErrorResponse) {
                switch (e.status) {
                    case 403: // Blacklisted
                    case 404: // Not found
                    case 422: // Unprocessable - captcha error
                    case 429: // Too many requests
                        return { authenticated: false, errorCode: e.status, errorMessage: e.message };
                }
            }
            console.error('AuthenticateByEmailService', e);
            throw e;
        }

        const dialogRef: MatDialogRef<RichVerifyDialogComponent> = this.dialogService.richVerify();
        dialogRef.componentInstance.email = email;

        // 3 attempts for input verification code
        for (let i = 0; i < 3; i++) {
            const authenticationEmailCode: string = await dialogRef.componentInstance.submitPromiseWithCode;

            if (authenticationEmailCode === null) {
                return { authenticated: false };
            }

            try {
                const gcToken = await this.recaptchaService.check('contacts/verify');
                const { verified, auth } = await this.contactService.verifyCaptcha({
                    contactId,
                    emailCode: authenticationEmailCode,
                    gcToken,
                });
                if (verified) {
                    dialogRef.close(true);

                    await this.userService.authenticateJwt(auth.accessToken);
                    return { authenticated: true };
                }
            } catch (e) {
                console.error(e);
            }

            dialogRef.componentInstance.verifiedState = false;
            dialogRef.componentInstance.resetDialog();
        }

        dialogRef.close(false);
        return { authenticated: false };
    }
}
